import { AxiosProgressEvent, AxiosResponse, CancelTokenSource } from 'axios';
import { action, runInAction, makeObservable } from 'mobx';
import { eventBus, subscribe } from 'mobx-event-bus2';

import { ActivityListResponse, ActivityResponse } from 'ui/types';
import { ActivityType } from 'utils/stores/Activities/constants';

import DetailsActivities from 'ui/stores/DetailsActivities';

import { PaginationParams } from 'utils/api/types';
import { UserSearchResponse } from 'utils/api/modules/user/user.types';

import RootStore from 'stores/Root';
import API from 'utils/api';
import { EventType } from 'utils/events/constants';
import Logger from 'utils/logger';

export default class ReporterActivities extends DetailsActivities<RootStore, typeof API> {
  eventId: string;

  constructor(rootStore: RootStore, api: typeof API, eventId: string) {
    super(rootStore, api);

    makeObservable(this);

    this.eventId = eventId;

    eventBus.register(this);
  }

  async loadActivitiesData(params: PaginationParams): Promise<AxiosResponse<ActivityListResponse>> {
    return this.api.entities.loadEntitySubmissionActivities('events', this.eventId, params)();
  }

  @action.bound
  async addMessage(message: string, taggedUsers?: UserSearchResponse[]): Promise<void> {
    if (this.error || !this.activities) {
      return;
    }
    try {
      const { data } = await this.api.entities.addEntitySubmissionMessage(
        'events',
        this.eventId,
        message,
        taggedUsers
      )();
      runInAction(() => {
        if (this.activities) {
          this.activities = [...this.activities, { ...data, type: ActivityType.Message }];
        }
      });
    } catch (e) {
      Logger.error(`Invalid add message API result: Event ID: ${this.eventId}. Message: ${message}`, e);
    }
  }

  async doUploadFile(
    file: File,
    onUpdateProgress: (event: AxiosProgressEvent) => void,
    cancelTokenSource: CancelTokenSource
  ): Promise<void> {
    try {
      const { data } = await this.api.entities.uploadEntitySubmissionFile(
        'events',
        this.eventId,
        file,
        onUpdateProgress,
        cancelTokenSource
        // false
      )();
      runInAction(() => {
        if (this.activities) {
          this.activities = [...this.activities, data as unknown as ActivityResponse];
        }
        this.rootStore.eventDetails.activities?.loadAttachments();
      });
    } catch (e) {
      Logger.error(`Invalid add file API result: Event ID: ${this.eventId}.`, e);
      this.rootStore.notification.enqueueErrorSnackbar('Failed to upload file');
    }
  }

  @action.bound
  async deleteMessage(messageId: string): Promise<void> {
    if (this.activities === undefined) {
      return;
    }
    const message = this.activities.find((activity) => activity.uuid === messageId);
    if (message) {
      try {
        await this.api.entities.removeEntitySubmissionMessage('events', this.eventId, messageId)();
        runInAction(() => {
          if (this.activities !== undefined) {
            this.activities = this.activities.filter((activity) => activity.uuid !== messageId);
          }
        });
      } catch (e) {
        this.rootStore.notification.enqueueErrorSnackbar(
          'Unable to delete the comment at this time. Please try again later'
        );
        Logger.error(`Invalid delete message API result: Event ID: ${this.eventId}. Message ID: ${messageId}`, e);
      }
    }
  }

  @action.bound
  async deleteFile(fileId: string): Promise<void> {
    if (this.activities === undefined) {
      return;
    }
    const file = this.activities.find((activity) => activity.uuid === fileId);
    if (file) {
      try {
        await this.api.entities.removeEntitySubmissionFile('events', this.eventId, fileId)();
        runInAction(() => {
          this.removeActivity(file.uuid);
        });
        this.rootStore.eventDetails.activities?.loadAttachments();
      } catch (e) {
        this.rootStore.notification.enqueueErrorSnackbar(
          'Unable to delete the file at this time. Please try again later'
        );
        Logger.error(`Invalid delete file API result: Event ID: ${this.eventId}. File ID: ${fileId}`, e);
      }
    }
  }

  @subscribe(EventType.CreatedActivity)
  @action
  createdActivity(): void {
    if (this.error || !this.activities) {
      return;
    }

    this.activities = undefined;
    this.loadActivities();
  }
}
